<div class="d-flex justify-content-between align-items-center mb-3">
    <h3 class="mb-0">Reconciliation</h3>
    <div class="d-flex gap-2">
        <button class="btn btn-outline-secondary btn-sm" (click)="toggleNeedsReview()" [class.active]="filterNeedsReview">
            Needs Review
        </button>
        <button class="btn btn-outline-secondary btn-sm" (click)="toggleOnHold()" [class.active]="filterOnHold">
            On Hold
        </button>
    </div>
</div>

<div class="row g-3">
    <div class="col-lg-5">
        <div class="purchasing-table-panel">
            <table class="table table-sm table-hover mb-0 recon-list">
                <thead>
                    <tr>
                        <th>Invoice #</th>
                        <th>PO #</th>
                        <th>Match</th>
                        <th>Resolution</th>
                    </tr>
                </thead>
                <tbody>
                    @for (result of filteredResults; track result.id) {
                        <tr
                            (click)="selectResult(result)"
                            [class.table-active]="selectedResult?.id === result.id"
                            style="cursor: pointer"
                        >
                            <td>{{ result.invoiceNumber || '—' }}</td>
                            <td>{{ result.poNumber || 'Unmatched' }}</td>
                            <td><span class="badge" [ngClass]="matchBadgeClass(result.matchStatus)">{{ result.matchStatus }}</span></td>
                            <td>
                                @if (result.resolution) {
                                    <span class="badge" [ngClass]="resolutionBadgeClass(result.resolution)">{{ result.resolution }}</span>
                                } @else {
                                    <span class="text-muted">—</span>
                                }
                            </td>
                        </tr>
                    }
                    @if (filteredResults.length === 0) {
                        <tr>
                            <td colspan="4" class="text-center text-muted py-3">No match results yet.</td>
                        </tr>
                    }
                </tbody>
            </table>
        </div>
    </div>
    <div class="col-lg-7">
        @if (selectedResult) {
            <div class="card">
                <div class="card-header d-flex justify-content-between align-items-center">
                    <span>
                        {{ selectedResult.invoiceNumber || 'Invoice' }}
                        <span class="badge ms-2" [ngClass]="matchBadgeClass(selectedResult.matchStatus)">{{ selectedResult.matchStatus }}</span>
                        @if (selectedResult.resolution) {
                            <span class="badge ms-1" [ngClass]="resolutionBadgeClass(selectedResult.resolution)">{{ selectedResult.resolution }}</span>
                        }
                    </span>
                    @if (canEdit) {
                        <div class="d-flex gap-2 flex-wrap">
                            <button class="btn btn-outline-secondary btn-sm" (click)="reEvaluate()" [disabled]="isProcessing">Re-evaluate</button>
                            @if (hasVariances(selectedResult) && !selectedResult.resolution) {
                                <button class="btn btn-outline-info btn-sm" (click)="acceptVariance()" [disabled]="isProcessing">Accept Variance</button>
                            }
                            @if (!selectedResult.resolution || selectedResult.resolution === 'AcceptedVariance') {
                                <button class="btn btn-success btn-sm" (click)="approve()" [disabled]="isProcessing">Approve</button>
                            }
                            <button class="btn btn-outline-danger btn-sm" (click)="hold()" [disabled]="isProcessing">Hold</button>
                            @if (hasVariances(selectedResult)) {
                                <button class="btn btn-outline-warning btn-sm" (click)="openContactVendor()">Contact Vendor</button>
                            }
                        </div>
                    }
                </div>
                <div class="card-body">
                    <dl class="row mb-2 small">
                        <dt class="col-sm-3">PO</dt>
                        <dd class="col-sm-9">
                            @if (selectedResult.poNumber) {
                                <a [routerLink]="['/purchasing/purchase-orders']">{{ selectedResult.poNumber }}</a>
                            } @else {
                                <span class="text-muted">Unmatched</span>
                            }
                        </dd>
                        <dt class="col-sm-3">Vendor</dt>
                        <dd class="col-sm-9">{{ selectedResult.vendorName || '—' }}</dd>
                        <dt class="col-sm-3">Equipment</dt>
                        <dd class="col-sm-9">{{ selectedResult.equipment || '—' }}</dd>
                        <dt class="col-sm-3">Work Order</dt>
                        <dd class="col-sm-9">{{ selectedResult.workOrder || '—' }}</dd>
                        <dt class="col-sm-3">Invoice Status</dt>
                        <dd class="col-sm-9">{{ selectedResult.invoiceStatus || '—' }}</dd>
                        <dt class="col-sm-3">Totals</dt>
                        <dd class="col-sm-9">
                            PO {{ selectedResult.poTotal | currency }} · Invoice {{ selectedResult.invoiceTotal | currency }} · Received
                            {{ selectedResult.receivedValue | currency }}
                        </dd>
                    </dl>

                    <div class="d-flex gap-3 small text-muted mb-3">
                        <span>
                            <a [routerLink]="['/purchasing/invoicing']" [queryParams]="{ orderId: selectedResult.purchaseOrderId }">View invoice</a>
                        </span>
                        @if (selectedResult.purchaseOrderId) {
                            <span>
                                <a [routerLink]="['/purchasing/receiving']" [queryParams]="{ orderId: selectedResult.purchaseOrderId }">View receipts</a>
                            </span>
                        }
                    </div>

                    @if (canEdit) {
                        <div class="mb-3">
                            <label class="form-label small mb-0">Resolution Notes</label>
                            <textarea class="form-control form-control-sm" rows="2" [(ngModel)]="resolutionNotes"></textarea>
                        </div>
                    }

                    <h6>Line Comparison</h6>
                    <div class="table-responsive">
                        <table class="table table-sm mb-0 line-item-table">
                            <thead>
                                <tr>
                                    <th>Description</th>
                                    <th class="text-end">Ordered</th>
                                    <th class="text-end">Received</th>
                                    <th class="text-end">Invoiced</th>
                                    <th class="text-end">PO Price</th>
                                    <th class="text-end">Inv Price</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                @for (line of selectedResult.lines; track line.id) {
                                    <tr [ngClass]="lineClass(line.lineStatus)">
                                        <td>{{ line.description }}</td>
                                        <td class="text-end">{{ line.quantityOrdered ?? '—' }}</td>
                                        <td class="text-end">{{ line.quantityReceived ?? '—' }}</td>
                                        <td class="text-end">{{ line.quantityInvoiced ?? '—' }}</td>
                                        <td class="text-end">{{ line.poUnitPrice | currency }}</td>
                                        <td class="text-end">{{ line.invoiceUnitPrice | currency }}</td>
                                        <td>{{ line.lineStatus }}</td>
                                    </tr>
                                }
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        } @else {
            <div class="text-muted p-4 text-center border rounded">Select a match result to review variances and take action.</div>
        }
    </div>
</div>

@if (emailing) {
    <div class="modal-backdrop-custom" (click)="emailing = false"></div>
    <div class="modal-dialog-custom">
        <div class="card">
            <div class="card-header">Contact Vendor About Variances</div>
            <div class="card-body">
                <div class="mb-2">
                    <label class="form-label small mb-0">To Email</label>
                    <input class="form-control form-control-sm" [(ngModel)]="emailModel.toEmail" />
                </div>
                <div class="mb-2">
                    <label class="form-label small mb-0">CC Email</label>
                    <input class="form-control form-control-sm" [(ngModel)]="emailModel.ccEmail" />
                </div>
                <div class="mb-2">
                    <label class="form-label small mb-0">Subject</label>
                    <input class="form-control form-control-sm" [(ngModel)]="emailModel.subject" />
                </div>
                <div class="mb-2">
                    <label class="form-label small mb-0">Message</label>
                    <textarea class="form-control form-control-sm" rows="3" [(ngModel)]="emailModel.message"></textarea>
                </div>
            </div>
            <div class="card-footer d-flex justify-content-end gap-2">
                <button class="btn btn-outline-secondary btn-sm" (click)="emailing = false">Cancel</button>
                <button class="btn btn-primary btn-sm" (click)="sendContactVendor()" [disabled]="!emailModel.toEmail">Send</button>
            </div>
        </div>
    </div>
}
